/**
 * The Watchlist as a register (roadmap D117): what the KPI cards count for
 * the kind shown, which tabs the form has for a kind, the words for a heatmap
 * cell and a band, who "Assign" suggests, and the risk settings as the
 * settings sheet edits them.
 *
 * DOM-free and tested (test/watchlistModel.test.ts). The scoring itself is
 * the shared model's (shared/risks/standard.ts), which the host and the
 * agents' tools use too.
 */
// Functions only: a tsx test cannot value-import a shared constant (it comes through as CommonJS).
import { type AppetiteLevel, appetiteOf, bandFor, fitsSuggestion, type HeatCell, registerCounts, reviewOverdue, type RoleSuggestion } from '@aioe/shared/risks/standard';
import { dateMedium } from '@aioe/shared/wording/dateTime';
import type { BugSeverity, RiskEntry, RiskKind, RiskSettings, RiskSettingsDraft, RiskTemplate } from '@aioe/shared';
import { isOpenStatus, type KindFilter, RATING_TEXT, ratingOf, type RiskRow } from './risksModel.ts';

/** Art.tsx's tones, restated: a DOM-free model cannot import a component file (the tests' tsconfig has no JSX). */
export type ArtTone = 'teal' | 'violet' | 'amber' | 'blue' | 'rose' | 'green' | 'slate';

// ── The KPI cards ────────────────────────────────────────────────────────

export interface Kpi {
  id: string;
  label: string;
  value: number;
  detail: string;
  tone: ArtTone;
  /** A band's colour, for the swatch beside its label (never the only signal: the label says it). */
  colour?: string;
  /** The one number that waits on a person. */
  needsYou?: boolean;
  /** Pressing it filters the list to these. */
  band?: string;
}

const BAND_TONES: readonly ArtTone[] = ['green', 'green', 'amber', 'amber', 'rose', 'rose'];

/** A band's tone by its place, lowest first: calm to loud. */
export function bandTone(index: number, count: number): ArtTone {
  if (count <= 1) return 'amber';
  return BAND_TONES[Math.round((index / (count - 1)) * (BAND_TONES.length - 1))] ?? 'amber';
}

const SEVERITY_TEXT: Record<BugSeverity, string> = { blocker: 'Blocker', critical: 'Critical', major: 'Major', minor: 'Minor', trivial: 'Trivial' };

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * MojoPSA's cards for the kind shown: Total, Open, Closed, then — for risks —
 * one per band of the template (highest first) and Unassessed; for issues
 * the four ratings; for bugs the five severities; for everything, open risks,
 * issues and bugs, what is above appetite and the reviews overdue.
 */
export function kpisFor(kind: KindFilter, rows: readonly RiskRow[], t: RiskTemplate, settings: RiskSettings | undefined, now: number, which: 'inherent' | 'residual' = 'residual'): Kpi[] {
  const shown = kind === 'all' ? rows : rows.filter((r) => r.entry.kind === kind);
  const entries = shown.map((r) => r.entry);
  const open = shown.filter((r) => r.open);
  const out: Kpi[] = [
    { id: 'total', label: 'Total', value: shown.length, detail: 'recorded', tone: 'slate' },
    { id: 'open', label: 'Open', value: open.length, detail: 'in hand', tone: 'blue' },
    { id: 'closed', label: 'Closed', value: shown.length - open.length, detail: 'settled', tone: 'green' },
  ];
  if (kind === 'risk') {
    const counts = registerCounts(entries, t, settings, now, which);
    const bands = [...t.bands].sort((a, b) => b.min - a.min);
    bands.forEach((b, i) => out.push({ id: `band:${b.id}`, label: b.label, value: counts.byBand[b.id] ?? 0, detail: `score ${b.min}–${b.max}`, tone: bandTone(bands.length - 1 - i, bands.length), colour: b.colour, band: b.id }));
    out.push({ id: 'unassessed', label: 'Unassessed', value: counts.unassessed, detail: counts.unassessed ? 'not on the matrix' : 'all rated', tone: 'slate', needsYou: counts.unassessed > 0 });
    return out;
  }
  if (kind === 'issue') {
    for (const r of ['critical', 'high', 'medium', 'low'] as const) out.push({ id: `rating:${r}`, label: RATING_TEXT[r], value: open.filter((x) => x.rating === r).length, detail: 'open', tone: r === 'critical' ? 'rose' : r === 'high' ? 'amber' : r === 'medium' ? 'blue' : 'green' });
    return out;
  }
  if (kind === 'bug') {
    const SEVERITIES: readonly BugSeverity[] = ['blocker', 'critical', 'major', 'minor', 'trivial'];
    for (const s of SEVERITIES) out.push({ id: `severity:${s}`, label: SEVERITY_TEXT[s], value: open.filter((x) => x.entry.severity === s).length, detail: 'open', tone: s === 'blocker' || s === 'critical' ? 'rose' : s === 'major' ? 'amber' : s === 'minor' ? 'blue' : 'green' });
    return out;
  }
  const counts = registerCounts(entries, t, settings, now, which);
  out.push(
    { id: 'risks', label: 'Risks', value: open.filter((r) => r.entry.kind === 'risk').length, detail: 'open', tone: 'amber' },
    { id: 'issues', label: 'Issues', value: open.filter((r) => r.entry.kind === 'issue').length, detail: 'open', tone: 'rose' },
    { id: 'bugs', label: 'Bugs', value: open.filter((r) => r.entry.kind === 'bug').length, detail: 'open', tone: 'violet' },
    { id: 'appetite', label: 'Above appetite', value: counts.aboveAppetite, detail: counts.aboveAppetite ? 'over what is accepted' : 'all within', tone: 'rose', needsYou: counts.aboveAppetite > 0 },
    { id: 'reviews', label: 'Reviews overdue', value: counts.reviewsOverdue, detail: counts.reviewsOverdue ? plural(counts.reviewsOverdue, 'review waits', 'reviews wait') : 'none waiting', tone: 'blue', needsYou: counts.reviewsOverdue > 0 },
  );
  return out;
}

// ── The heatmap ──────────────────────────────────────────────────────────

/** "Likely × Major: score 16, High — 2 risks" for a cell's label and its tooltip. */
export function cellWords(cell: HeatCell, t: RiskTemplate, impactScale: RiskTemplate['impact']): string {
  const l = t.likelihood.find((s) => s.level === cell.likelihood)?.label ?? String(cell.likelihood);
  const i = impactScale.find((s) => s.level === cell.impact)?.label ?? String(cell.impact);
  return `${l} × ${i}: score ${cell.score}${cell.band ? `, ${cell.band.label}` : ''} — ${cell.uids.length ? plural(cell.uids.length, 'risk', 'risks') : 'none'}`;
}

/** The rows a band card filters to: open risks whose current assessment (on `which`) falls in the band. */
export function uidsInBand(rows: readonly RiskRow[], t: RiskTemplate, band: string, which: 'inherent' | 'residual'): string[] {
  return rows
    .filter((r) => r.open && r.entry.kind === 'risk')
    .filter((r) => {
      const a = which === 'inherent' ? r.entry.inherent : (r.entry.residual ?? r.entry.inherent);
      return a?.score ? bandFor(a.score, t)?.id === band : false;
    })
    .map((r) => r.entry.uid);
}

/** Black or white ink on a band's colour, whichever reads better (WCAG's luminance midpoint, as MojoPSA's `readableTextColour`). */
export function inkOn(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '#111827';
  const v = parseInt(m[1], 16);
  const [r, g, b] = [(v >> 16) & 255, (v >> 8) & 255, v & 255].map((c) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.179 ? '#111827' : '#FFFFFF';
}

// ── The form's tabs ──────────────────────────────────────────────────────

export type FormTab = 'details' | 'assessment' | 'controls' | 'bug' | 'notes' | 'relationships';

export const TAB_TEXT: Record<FormTab, string> = { details: 'Details', assessment: 'Assessment', controls: 'Controls', bug: 'Reproduce', notes: 'Notes', relationships: 'Relationships' };

/**
 * The form's tabs: a risk's assessment and controls; an issue neither; a bug its own tab for how to
 * reproduce it. No Metadata tab: who raised and last changed a saved entry is one line under the form
 * (`recordLine`), not a page of its own.
 */
export function tabsFor(kind: RiskKind, _mode: 'new' | 'edit'): Array<{ id: FormTab; label: string; isDisabled?: boolean }> {
  const ids: FormTab[] = kind === 'risk' ? ['details', 'assessment', 'controls', 'notes', 'relationships'] : kind === 'bug' ? ['details', 'bug', 'notes', 'relationships'] : ['details', 'notes', 'relationships'];
  return ids.map((id) => ({ id, label: TAB_TEXT[id] }));
}

/** What an entry is about, offered under Category (any word can be typed). */
export const CATEGORIES = ['security', 'architecture', 'delivery', 'quality', 'cost', 'schedule', 'people', 'compliance', 'operations'] as const;

// ── Assign ───────────────────────────────────────────────────────────────

/** The assignee choice "Assign" opens on: a specialist (else a member) whose role fits the suggestion, else the dialog's own default. */
export function suggestedAssignKey(choices: ReadonlyArray<{ key: string; label: string; detail?: string; group: string }>, s: RoleSuggestion): string | undefined {
  const fits = (c: { label: string; detail?: string }) => fitsSuggestion(s, { role: c.detail ?? '', name: c.label });
  return choices.find((c) => c.group === 'specialist' && fits(c))?.key ?? choices.find((c) => c.group === 'member' && fits(c))?.key;
}

// ── The settings sheet ───────────────────────────────────────────────────

export interface SettingsForm {
  templateId: string;
  organisation: string;
  project: string;
  /** This repository's own appetite ("" = the project's), on a repository's view. */
  repository: string;
  reviewEveryDays: number;
}

/** The settings as the sheet edits them, for one repository (`rootId`, "" on the project's view). */
export function settingsForm(settings: RiskSettings | undefined, t: RiskTemplate, rootId: string): SettingsForm {
  const a = appetiteOf(settings, t, rootId);
  return {
    templateId: settings?.templateId ?? t.id,
    organisation: a.organisation,
    project: a.project,
    repository: rootId ? (settings?.repositories?.[rootId]?.appetite ?? '') : '',
    reviewEveryDays: settings?.reviewEveryDays ?? t.reviewEveryDays ?? 30,
  };
}

/** What the sheet saves: the settings with the form laid over them (another repository's own settings kept). */
export function settingsDraft(before: RiskSettings | undefined, f: SettingsForm, rootId: string): RiskSettingsDraft {
  const { version: _v, updatedAt: _u, updatedBy: _b, ...kept } = before ?? {};
  const repositories = { ...(kept.repositories ?? {}) };
  if (rootId) {
    const own = { ...(repositories[rootId] ?? {}) };
    if (f.repository) own.appetite = f.repository;
    else delete own.appetite;
    if (Object.keys(own).length) repositories[rootId] = own;
    else delete repositories[rootId];
  }
  return {
    ...kept,
    templateId: f.templateId,
    appetite: { ...(kept.appetite ?? {}), organisation: f.organisation, project: f.project },
    ...(Object.keys(repositories).length ? { repositories } : { repositories: undefined }),
    reviewEveryDays: Math.max(1, Math.min(730, Math.round(f.reviewEveryDays) || 30)),
  };
}

export const APPETITE_HINT: Record<AppetiteLevel, string> = {
  organisation: 'The most the organisation accepts anywhere. Until it publishes its own, it is kept with the project.',
  project: 'The most the project accepts for its own risks, and for a repository that sets none.',
  repository: 'The most this repository accepts. Empty: the project’s.',
};

// ── Small words ──────────────────────────────────────────────────────────

/** "Review overdue" / "review due 4 Nov", for an entry's line. Undefined without a date or once settled. */
export function reviewWords(e: Pick<RiskEntry, 'status'> & { reviewDue?: string }, now: number): { text: string; overdue: boolean } | undefined {
  if (!e.reviewDue || !isOpenStatus(e.status)) return undefined;
  const overdue = reviewOverdue(e, now);
  const d = new Date(`${e.reviewDue.slice(0, 10)}T00:00:00Z`);
  const when = Number.isNaN(d.getTime()) ? e.reviewDue : dateMedium(d, { year: false, timeZone: 'UTC' });
  return { text: overdue ? `review overdue since ${when}` : `review ${when}`, overdue };
}

/** A row's rating as the rest of the register words it. */
export function rowRating(r: Pick<RiskRow, 'entry'>): string {
  return RATING_TEXT[ratingOf(r.entry)];
}
